import { Component, Watch } from 'vue-property-decorator';
import Base from '../Base';
import TopNav from '@/components/topnav/TopNav.vue';
import { StatusSwitch, Button, Loading, Selector } from 'streamlabs-beaker';
import ConfirmationModal from '@/components/modals/ConfirmationModal.vue';
import { EModalName } from '@/helpers/modal';
import SubscriptionApi from '@/apis/subscription-api';
import NotificationHelper from '@/helpers/notification';
import { ESubscriptionStep, TSubscriptionPlanType, TSubscriptionStep } from '@/helpers/subscription';
import CheckoutPlans from '@/components/checkout/CheckoutPlans.vue';
import UtilHelper, { IOriginSelector, ITab } from '@/helpers/utils';
import SwitchPlanModal from '@/components/checkout/SwitchPlanModal.vue';
import UpdatePaymentMethodModal from '@/components/checkout/UpdatePaymentMethodModal.vue';
import AccountInvoices from '@/components/account/AccountInvoices.vue';
import { EUserAdEvent } from '@/helpers/user';
import UserApi from '@/apis/user-api';
import LatencyHelper, { DEFAULT_REGION } from '@/helpers/latency';
import SetupSLIDBanner from '@/components/topnav/SetupSLIDBanner.vue';
import { UltraCheckoutBanner } from '@streamlabs-core/sl-one-fe-components-vue2';
import { ESocketEvent } from '@/helpers/socket';
import AnalyticHelper, { EAnalyticsEvent } from '@/helpers/analytic';
import { Throttle } from 'lodash-decorators';

enum EPaymentMethodType {
  paypal = 'PayPal',
  visa = 'Visa',
  masterCard = 'MasterCard',
}

@Component({
  components: {
    TopNav,
    SStatusSwitch: StatusSwitch,
    SButton: Button,
    SLoading: Loading,
    ConfirmationModal,
    CheckoutPlans,
    SwitchPlanModal,
    UpdatePaymentMethodModal,
    AccountInvoices,
    SSelector: Selector,
    SetupSLIDBanner,
    UltraCheckoutBanner,
  },
})
export default class AccountSettings extends Base {
  regionOption = DEFAULT_REGION;
  regions: IOriginSelector[] = [];

  async mounted() {
    await this.loadPaymentMethod();
    await this.checkRegionsLatency();
    await this.setRegion();

    if (!this.userHasUltra && !this.userSubscriptionIsValid) this.$sla.sendEvent(EAnalyticsEvent.ultraBannerShown);
  }

  @Watch('userSubscription')
  async loadPaymentMethod() {
    const response = await SubscriptionApi.getPaymentMethod();

    if (response.paymentMethod) {
      this.userPaymentMethod = response.paymentMethod;
    }
  }

  get formattedExistingPaymentMethod(): string {
    if (this.userPaymentMethod.type === EPaymentMethodType.paypal) {
      const [emailAccount, emailDomain] = this.userPaymentMethod.email.split('@');

      const formattedPayPalAccount = `${emailAccount.substring(0, 2)}***${emailAccount.substring(
        emailAccount.length - 4,
        emailAccount.length,
      )}@${emailDomain}`;

      return formattedPayPalAccount;
    }

    return `**** ${this.userPaymentMethod.last4}`;
  }

  get cancelSubConfirmationModalName(): string {
    return EModalName.cancelSubscriptionConfirmation;
  }

  get userSubscriptionPlan(): string {
    if (!this.userSubscription || (this.userSubscription && this.subscriptionExpired)) return this.userHasUltra ? 'Ultra' : '';

    return this.userSubscription.plan_type;
  }

  get subscriptionNextBillingDate(): string {
    if (!this.userSubscription) return '';

    return this.userSubscription.expires_at_human_friendly;
  }

  get subscriptionExpiresAtDate(): string {
    if (!this.userSubscription) return '';

    return UtilHelper.dateToHumanReadable(this.userSubscription.expires_at);
  }

  get subscriptionExpired(): boolean {
    return this.userSubscription.status !== 'Active' && !UtilHelper.dateIsFuture(this.userSubscription.expires_at);
  }

  get subscriptionExpireText(): string {
    return `Your plan ${this.subscriptionExpired ? 'has expired' : 'will expire'} on ${this.subscriptionExpiresAtDate}`;
  }

  get disconnectSLConfirmationName(): string {
    return EModalName.disconnectSLConfirmation;
  }

  get disconnectStreamlabsIdentityConfirmation(): string {
    return EModalName.disconnectSLIDConfirmation;
  }

  get confirmationModalWidth(): number {
    if (!this.isMobile) return 700;

    return 400;
  }

  get renewPeriod(): string {
    if (this.userSubscription.plan_type === 'annual') return 'annually';

    return 'monthly';
  }

  get availableRegions(): IOriginSelector[] {
    return this.regions
      .map((region): IOriginSelector => {
        return {
          label: region.label,
          value: region.value,
          iconClass: region.iconClass,
          ping: region.ping
        };
      });
  }

  get userSettings() {
    return this.userModule.user.settings;
  }

  get twitchReauthenticationNeeded(): boolean {
    return this.currentUser?.twitchReauthenticationNeeded;
  }

  openCancelConfirmationModal() {
    this.$modal.show(this.cancelSubConfirmationModalName);
  }

  dismissCancelConfirmationModal() {
    this.$modal.hide(this.cancelSubConfirmationModalName);
  }

  async cancelPlan() {
    const response = await SubscriptionApi.cancel();

    if (!response.success) {
      NotificationHelper.error(response.message);
      return;
    }

    AnalyticHelper.sendEvent(EAnalyticsEvent.subscriptionCanceled);
    UtilHelper.trackGTMEvent({ event: EUserAdEvent.cancel });

    this.userSubscription = response.subscription;
  }

  opeCheckoutModalMiniPayment() {
    this.$modal.show(EModalName.checkoutMini, { step: ESubscriptionStep.payment });
  }

  openUpdatePaymentMethodModal() {
    this.$modal.show(EModalName.updatePaymentMethod);
  }

  openDisconnectConfirmationModal(modalName: EModalName) {
    this.$modal.show(modalName);
  }

  hideModal(modalName: EModalName) {
    this.$modal.hide(modalName);
  }

  async disconnectStreamlabs() {
    await UserApi.disconnectStreamlabsUser();

    this.userSLWidgetToken = null;

    this.hideModal(EModalName.disconnectSLConfirmation);
  }

  async disconnectStreamlabsIdentity() {
    const response = await UserApi.disconnectStreamlabsIdentity();

    if (response.success) {
      await this.loadUser(true);
      NotificationHelper.success('Your Streamlabs ID is disconnected.');
    }

    this.hideModal(EModalName.disconnectSLIDConfirmation);
  }

  setRegion() {
    let currentRegion = this.userSettings?.region;

    if (!currentRegion) currentRegion = DEFAULT_REGION.value;

    this.regionOption = this.availableRegions.find(
      (region) => region.value === currentRegion,
    );
  }

  async onRegionChange() {
    this.userModule.updateUserSettings({ region: this.regionOption.value });
    await this.updateUserSettings();
  }

  async checkRegionsLatency() {
    this.regions = await LatencyHelper.getRegionsWithLatency();
  }

  connectTwitch(reconnect = false) {
    const query = {
      go_to: '/account',
    };

    if (reconnect) query['reconnect'] = 'true';

    this.$router.push({
      path: '/connect-platforms',
      query,
    });
  }

  connectStreamlabsWidgets() {
    const newWindow = window.open();
    UserApi.getStreamlabsAuthUrl().then(response => {
      const url = response.url;
      newWindow.location = url;

      window.addEventListener('message', async (message) => {
        if (message.data && message.data.type === 'callback-success' && message.data.from === 'streamlabs') {
          await this.loadUser(true);
          this.$sla.sendEvent(EAnalyticsEvent.streamlabsConnected);

          if (this.userSLWidgetToken) {
            NotificationHelper.success('👍  Streamlabs connected!');
            this.sendSocketEvent(ESocketEvent.updateSLWidgetToken);
          }
        }
      }, { once: true });
    });
  }

  get marketingEmailsSubscribed(): boolean {
    return this.userSettings?.marketingEmailsSubscribed;
  }

  set marketingEmailsSubscribed(value) {
    this.userModule.updateUserSettings({ marketingEmailsSubscribed: value });
    this.updateUserSettings();
  }

  @Throttle(500, { leading: false, trailing: true })
  async updateUserSettings() {
    await UserApi.saveUserSettings(this.userSettings);
  }
}
